import React from 'react';
import { Box, Typography, Popover } from '@mui/material';
import { formatAddressDisplay } from '../utils/addressFormatters';
import { isValidCompanyStatus } from '../constants/entityStatusConstants';

const styles = {
  title: {
    fontWeight: 600,
    fontSize: '1.1rem',
    mb: 1.5,
    color: '#1a1a1a'
  },
  rowContainer: {
    display: "flex",
    gap: "50px"
  },
  fieldContainer: {
    mb: 1
  },
  fieldLabel: {
    color: '#666',
    fontSize: '0.875rem',
    mb: 0.5,
    fontWeight: 500
  },
  fieldValue: {
    fontSize: '0.95rem',
    fontWeight: 500,
    color: '#333'
  },
  confidenceValue: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#333'
  },
  confidenceContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },
  confidenceIndicator: {
    width: 8,
    height: 8,
    borderRadius: '50%'
  },
  popoverPaper: {
    borderRadius: 2,
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -8,
      left: '50%',
      transform: 'translateX(-50%)',
      width: 0,
      height: 0,
      borderLeft: '8px solid transparent',
      borderRight: '8px solid transparent',
      borderBottom: '8px solid white',
    }
  },
  popoverContent: {
    width: 380,
    p: 2.5
  }
};

const NodeDetails = ({ node, anchorEl, onClose }) => {
  // Format the type display
  const getTypeDisplay = (type) => {
    switch (type) {
      case 'COMPANY': return 'COMPANY';
      case 'PERSON': return 'PERSON';
      case 'ADDRESS': return 'ADDRESS';
      default: return type?.replace('_', ' ').toUpperCase() || 'UNKNOWN';
    }
  };

  const getContent = () => {
    if (!node) return null;
    console.log(node);

    if (node.type === 'COMPANY') {
      return (
        <Box sx={styles.rowContainer}>
          {/* Grid Container */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 3,
            marginBottom: 2
          }}>
            {/* Row 1 - Item 1 */}
            <Box sx={styles.fieldContainer}>
              <Typography variant="body2" sx={styles.fieldLabel}>
                Type
              </Typography>
              <Typography variant="body1" sx={styles.fieldValue}>
                {getTypeDisplay(node.type)}
              </Typography>
            </Box>

            {/* Row 1 - Item 2 */}
            <Box sx={styles.fieldContainer}>
              <Typography variant="body2" sx={styles.fieldLabel}>
                Incorporation date
              </Typography>
              <Typography variant="body1" sx={styles.fieldValue}>
                {node.data?.incorporation_date ? new Date(node.data.incorporation_date).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>

            {/* Row 2 - Item 1 */}
            <Box sx={styles.fieldContainer}>
              <Typography variant="body2" sx={styles.fieldLabel}>
                State ID
              </Typography>
              <Typography variant="body1" sx={styles.fieldValue}>
                {node.data?.state_id || 'N/A'}
              </Typography>
            </Box>
            {/* Row 2 - Item 2 */}
            <Box sx={styles.fieldContainer}>
              <Typography variant="body2" sx={styles.fieldLabel}>
                Status
              </Typography>
              <Box
                sx={{
                  backgroundColor: isValidCompanyStatus(node.data?.status) ? '#4caf50' : '#f44336',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '20px',
                  display: 'inline-block',
                  fontSize: '0.675rem',
                  fontWeight: 500,
                  textAlign: 'center',
                  minWidth: '60px'
                }}
              >
                {node.data.status}
              </Box>
            </Box>

            {/* Row 2 - Item 3 */}
            <Box sx={styles.fieldContainer}>
              <Typography variant="body2" sx={styles.fieldLabel}>
                State Code
              </Typography>
              <Typography variant="body1" sx={styles.fieldValue}>
                {node.data?.state_code || 'N/A'}
              </Typography>
            </Box>
          </Box>
        </Box>
      )
    }
    else if (node.type === 'ADDRESS') {
      return (
        <Box>
        </Box>
      )
    }
    else if (node.type === 'PERSON') {
      return (
        <Box sx={styles.rowContainer}>
          {/* Grid Container */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 4,
            marginBottom: 2
          }}>
            {/* Row 1 - Item 1 */}
            <Box sx={styles.fieldContainer}>
              <Typography variant="body2" sx={styles.fieldLabel}>
                Type
              </Typography>
              <Typography variant="body1" sx={styles.fieldValue}>
                {getTypeDisplay(node.type)}
              </Typography>
            </Box>
            {/* Row 1 - Item 2 */}
            <Box sx={{ ...styles.fieldContainer, gridColumn: 'span 2' }}>
              <Typography variant="body2" sx={styles.fieldLabel}>
                Role
              </Typography>
              <Typography variant="body1" sx={styles.fieldValue}>
                {node.data?.role || 'N/A'}
              </Typography>
            </Box>
          </Box>
        </Box>
      )
    }
  }


  const renderNodeContent = () => {
    if (!node) return null;

    const isAddress = node.type === 'ADDRESS';
    const addressFormat = isAddress ? formatAddressDisplay(node.label) : null;

    return (
      <Box>
        {/* Node Name */}
        <Typography variant="h6" sx={styles.title}>
          {isAddress ? (
            <Box>
              <Box>{addressFormat.line1}</Box>
              {addressFormat.line2 && (
                <Box>{addressFormat.line2}</Box>
              )}
            </Box>
          ) : (
            node.label
          )}
        </Typography>
        {/* First Row - 3 items */}
        {getContent()}
      </Box>
    );
  };

  return (
    <Popover
      open={!!node}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      PaperProps={{
        sx: styles.popoverPaper
      }}
    >
      <Box sx={styles.popoverContent}>
        {renderNodeContent()}
      </Box>
    </Popover>
  );
};

export default NodeDetails;